/**
 * @file Pure helpers for the persistent-boot-failure surface (#1593).
 * @module @labcraft/lab/ui/lib/boot-trouble
 *
 * When a shell repeatedly fails to start at the hypervisor hop
 * (`UPSTREAM_FAILED` / 4004), the chapter page must stop looping the resumable
 * "dozed off / Wake it up" surface — which implies a transient blip — and show
 * an honest "trouble starting your shell" recovery. This module holds the pure
 * pieces that decision shares: the consecutive-failure tally (rapid failures
 * accumulate; a gap resets) and the honest copy the takeover renders. Keeping
 * them off the component makes the load-bearing loop-detection logic
 * unit-testable without mounting the page.
 *
 * Free functions (not class methods), per the sibling `lib/budget.ts`
 * pure-helper convention.
 */

/**
 * Consecutive `UPSTREAM_FAILED` (4004) closes for one machine that flip the
 * recovery from "dozed off" to the honest "trouble starting" surface. A single
 * transient hypervisor blip heals via the one silent reconnect and never
 * reaches this; a persistent server-side wedge (the cgroup incident) trips it
 * in seconds instead of looping "Wake it up" forever.
 */
export const UPSTREAM_FAILURE_CAP = 3

/**
 * Consecutive-failure window: two closes more than this apart are unrelated
 * blips (the tally resets to 1), so only a rapid boot-fail loop trips
 * {@link UPSTREAM_FAILURE_CAP}.
 */
export const UPSTREAM_FAILURE_WINDOW_MS = 60_000

/**
 * A machine's rolling 4004 tally.
 *
 * @property count - consecutive failures within the window.
 * @property at - epoch-ms of the most recent failure.
 */
export type UpstreamFailureTally = { count: number; at: number }

/**
 * Fold a new 4004 close into the prior tally. A failure within
 * {@link UPSTREAM_FAILURE_WINDOW_MS} of the last increments the count; a later
 * one (or the first) resets to 1. Pure — the caller owns the per-machine store.
 *
 * @param prior - the machine's previous tally, or undefined for the first close.
 * @param now - epoch-ms of this close.
 * @returns the updated tally.
 */
export function bumpUpstreamFailure(
  prior: UpstreamFailureTally | undefined,
  now: number,
): UpstreamFailureTally {
  const consecutive =
    prior !== undefined && now - prior.at < UPSTREAM_FAILURE_WINDOW_MS
  return { count: consecutive ? prior.count + 1 : 1, at: now }
}

/**
 * Whether a tally has reached the cap — the boot is persistently wedged and the
 * honest "trouble starting" surface should replace the "dozed off" loop.
 *
 * @param tally - the machine's current tally.
 * @returns true once the cap is reached.
 */
export function isBootWedged(tally: UpstreamFailureTally): boolean {
  return tally.count >= UPSTREAM_FAILURE_CAP
}

/**
 * Copy for the "trouble starting your shell" takeover — honest that the fault
 * is server-side and the loop is bounded, deliberately NOT the "dozed off /
 * Wake it up" reconnect copy (which implies a transient blip). Structured as
 * data (like `evictionCopy`) so the shipped strings are assertable.
 *
 * @property eyebrow - short kicker.
 * @property title - honest headline.
 * @property body - reassurance + guidance.
 * @property cta - the retry button label.
 */
export const BOOT_TROUBLE_COPY = {
  eyebrow: 'Reconnect',
  title: 'We’re having trouble starting your shell',
  body: 'Something on our end is stopping your shell from starting — your chapter progress is saved. Give it another try; if it keeps happening, come back in a few minutes.',
  cta: 'Try again',
} as const
